import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { MatTabGroup } from "@angular/material/tabs";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { ModalMaterialComponent } from "../../../shared/components/modal/modal-material.component";
import {
  getModelFeatures,
  PhoneFeatures,
  PhoneMake
} from "../../../../../common/interfaces/phone-info";
import { PhoneItemInfo } from "../phone-admin-page.component";
import { TranslateService } from "@ngx-translate/core";

export interface LineInfo {
  name: string;
  aor?: string;
  type?: "Phone line" | "Busy lamp";
  /** enabled means call waiting is enabled  */
  enabled?: "enabled" | "disabled" | "*disabled";
  line?: string;
}

@Component({
  selector: "onsip-phone-detail",
  templateUrl: "./phone-detail.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PhoneDetailComponent implements OnChanges, OnDestroy {
  @ViewChild(MatTabGroup) matTabGroup!: MatTabGroup;
  @Input() phoneInfo!: PhoneItemInfo;
  lines: Array<LineInfo> = [];
  // show name of expansion module if being used
  expansionModuleName = "";
  phoneLinesView = new BehaviorSubject<"view" | "edit">("view");
  phoneInfoView = new BehaviorSubject<"view" | "edit">("view");
  phoneFeatures!: PhoneFeatures;
  noFeatures = false;
  private prevSelectedIndex = 0;
  private mode: "view" | "edit" = "view";
  private unsubscriber = new Subscription();

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnChanges() {
    this.phoneFeatures = getModelFeatures(this.phoneInfo.make as PhoneMake, this.phoneInfo.model);
    this.noFeatures =
      !this.phoneFeatures.Directory &&
      !this.phoneFeatures.Intercom &&
      !this.phoneFeatures.NATKeepalive &&
      !this.phoneFeatures.EasyTransfer &&
      !this.phoneFeatures.WebInterface &&
      !this.phoneFeatures.EndToEndEncryption &&
      !this.phoneFeatures.DigitMap;
    const linesInfo: Array<LineInfo> = [];

    this.phoneInfo.assignedUserAddresses.forEach(line => {
      const lineInfo = {
        name: line.userAddress.name,
        aor: line.userAddress.aor
      };
      if (line.callsPerLine || line.busyLampField) {
        // line is for phone calls
        if (line.callsPerLine) {
          const callLine = {
            type: "Phone line",
            // phone allows call waiting if callsPerLine is 0
            enabled: line.callsPerLine === "0" ? "enabled" : "disabled",
            line: line.line
          };
          const callLineInfo = { ...lineInfo, ...callLine } as LineInfo;
          linesInfo.push(callLineInfo);
        }
        if (line.busyLampField) {
          const blfLine = {
            type: "Busy lamp",
            enabled: "*disabled",
            line: line.busyLampField.line
          };
          const blfLineInfo = { ...lineInfo, ...blfLine } as LineInfo;
          linesInfo.push(blfLineInfo);
        }
        linesInfo.sort((a, b) => parseInt(a.line || "") - parseInt(b.line || ""));
      } else {
        // shouldn't be possible to reach here
        throw new Error("Invalid line. line is for neither phones or busy lamp field");
      }
    });
    this.lines = linesInfo;

    // determine whether an expansion module is being used
    // only phones with BLF feature have expansion modules
    // if the largest blf line assigned is greater than the default BLF limit than the phone system is using an expansion module
    // + 1 to BLF because the first line will always be a phoneline only
    if (
      this.phoneFeatures.BLF &&
      this.findLargestBLFLine(this.lines) > this.phoneFeatures.BLF + 1
    ) {
      this.expansionModuleName = this.phoneFeatures.SideCar
        ? this.phoneFeatures.SideCar[0].Type
        : "";
    } else {
      this.expansionModuleName = "";
    }
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  toggleLinesPanel(panel: "view" | "edit"): void {
    this.phoneLinesView.next(panel);
    this.mode = panel;
  }

  toggleInfoPanel(panel: "view" | "edit"): void {
    this.phoneInfoView.next(panel);
    this.mode = panel;
  }

  onSelectedIndexChange(event: number) {
    if (this.mode === "edit" && this.matTabGroup.selectedIndex !== this.prevSelectedIndex) {
      this.matTabGroup.selectedIndex = this.prevSelectedIndex;
      const modal = this.dialog.open(ModalMaterialComponent, {
        panelClass: ["mat-typography", "onsip-dialog-universal-style"],
        data: {
          title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
          message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
          primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
          secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
          primaryBtnFlat: true
        }
      });

      this.unsubscriber.add(
        modal
          .afterClosed()
          .pipe(take(1))
          .subscribe(retObj => {
            if (retObj && retObj.doPrimaryAction) {
              this.mode = "view";
              this.prevSelectedIndex = this.matTabGroup.selectedIndex || 0;
              this.matTabGroup.selectedIndex = event;
              // make sure both tabs are in view mode if user decides to navigate away
              this.phoneInfoView.next(this.mode);
              this.phoneLinesView.next(this.mode);
              this.cdRef.markForCheck();
            } else if (retObj && !retObj.doPrimaryAction) {
              this.mode = "edit"; // pressed "keep editing"
            }
          })
      );
    } else {
      this.mode = "view";
      this.prevSelectedIndex = this.matTabGroup.selectedIndex || 0;
      this.matTabGroup.selectedIndex = event;
    }
  }

  private findLargestBLFLine(lines: Array<LineInfo>): number {
    let maxLine = 0;
    lines.forEach(line => {
      if (line.type === "Busy lamp" && line.line && parseInt(line.line) > maxLine) {
        maxLine = parseInt(line.line);
      }
    });
    return maxLine;
  }
}
